import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { mobileStyles } from '../styles';

interface MobileImageCarouselProps {
	images: Api.IFileAttachment[];
	onImageRemove?: (imageId: string) => void;
	readOnly?: boolean;
}

const MobileImageCarousel: React.FC<MobileImageCarouselProps> = ({ images, onImageRemove, readOnly = false }) => {
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);
	const [fullscreenIndex, setFullscreenIndex] = React.useState(0);

	if (!images || images.length === 0) {
		return null;
	}

	const goToPrevious = () => {
		setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
	};

	const goToNext = () => {
		setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
	};

	const goToSlide = (index: number) => {
		setCurrentIndex(index);
	};

	const handleRemoveImage = (imageId: string) => {
		if (readOnly || !onImageRemove) return;
		onImageRemove(imageId);

		// Adjust current index if we removed the current image
		if (currentIndex >= images.length - 1) {
			setCurrentIndex(Math.max(0, images.length - 2));
		}
	};

	const openFullscreen = (index: number) => {
		setFullscreenIndex(index);
		setIsFullscreenOpen(true);
		// Prevent body scroll
		document.body.style.overflow = 'hidden';
	};

	const closeFullscreen = () => {
		setIsFullscreenOpen(false);
		// Restore body scroll
		document.body.style.overflow = '';
	};

	const goToFullscreenNext = () => {
		setFullscreenIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
	};

	const goToFullscreenPrevious = () => {
		setFullscreenIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
	};

	const currentImage = images[currentIndex];

	return (
		<div className={css(mobileStyles.imageCarouselContainer)}>
			<div className={css(mobileStyles.imageCarouselImageContainer)}>
				<img
					src={currentImage.url}
					alt={currentImage.fileName || 'Post image'}
					className={css(mobileStyles.imageCarouselImage)}
					onClick={() => openFullscreen(currentIndex)}
				/>

				{!readOnly && onImageRemove && (
					<button
						className={css(mobileStyles.imageCarouselRemoveButton)}
						onClick={() => handleRemoveImage(currentImage.id)}
						aria-label='Remove image'
					>
						×
					</button>
				)}

				{images.length > 1 && (
					<>
						<button
							className={css(mobileStyles.imageCarouselNavButton, mobileStyles.imageCarouselPrevButton)}
							onClick={goToPrevious}
							aria-label='Previous image'
						>
							‹
						</button>
						<button
							className={css(mobileStyles.imageCarouselNavButton, mobileStyles.imageCarouselNextButton)}
							onClick={goToNext}
							aria-label='Next image'
						>
							›
						</button>
					</>
				)}
			</div>

			{images.length > 1 && (
				<div className={css(mobileStyles.imageCarouselIndicators)}>
					{images.map((_, index) => (
						<button
							key={index}
							className={css(
								mobileStyles.imageCarouselIndicator,
								index === currentIndex && mobileStyles.imageCarouselIndicatorActive
							)}
							onClick={() => goToSlide(index)}
							aria-label={`Go to image ${index + 1}`}
						/>
					))}
				</div>
			)}

			{images.length > 1 && (
				<div className={css(mobileStyles.imageCarouselCounter)}>
					{currentIndex + 1} of {images.length}
				</div>
			)}

			{/* Fullscreen Modal */}
			{isFullscreenOpen && (
				<div className={css(mobileStyles.fullscreenModal)} onClick={closeFullscreen}>
					<div className={css(mobileStyles.fullscreenHeader)}>
						<button
							className={css(mobileStyles.fullscreenCloseButton)}
							onClick={closeFullscreen}
							aria-label='Close fullscreen'
						>
							×
						</button>
						{images.length > 1 && (
							<div className={css(mobileStyles.fullscreenCounter)}>
								{fullscreenIndex + 1} of {images.length}
							</div>
						)}
					</div>

					<div className={css(mobileStyles.fullscreenImageContainer)}>
						<img
							src={images[fullscreenIndex].url}
							alt={images[fullscreenIndex].fileName || 'Post image'}
							className={css(mobileStyles.fullscreenImage)}
							onClick={e => e.stopPropagation()}
						/>
					</div>

					{images.length > 1 && (
						<>
							<button
								className={css(mobileStyles.fullscreenNavButton, mobileStyles.fullscreenPrevButton)}
								onClick={e => {
									e.stopPropagation();
									goToFullscreenPrevious();
								}}
								aria-label='Previous image'
							>
								‹
							</button>
							<button
								className={css(mobileStyles.fullscreenNavButton, mobileStyles.fullscreenNextButton)}
								onClick={e => {
									e.stopPropagation();
									goToFullscreenNext();
								}}
								aria-label='Next image'
							>
								›
							</button>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export { MobileImageCarousel };
